import { format, parseISO, isValid } from "date-fns";

/**
 * Formats a date to YYYY-MM-DD format for HTML date input fields
 * @param date - Date object, ISO date string, timestamp, or null/undefined
 * @returns Formatted date string (YYYY-MM-DD) or empty string if invalid
 */
export const formatDateForInput = (
  date: Date | string | number | null | undefined
): string => {
  if (!date) return "";
  try {
    const dateObj = typeof date === "string" ? parseISO(date) : new Date(date);
    if (!isValid(dateObj)) return "";
    return format(dateObj, "yyyy-MM-dd");
  } catch {
    return "";
  }
};

/**
 * Formats an ISO date string or timestamp to a readable date format
 * @param dateString - ISO date string (e.g., "2025-11-06T17:30:37.906Z") or timestamp string
 * @returns Formatted date string (e.g., "November 6th, 2025") or "N/A" if invalid
 */
export function formatTimestampToDate(
  dateString: string | null | undefined
): string {
  // if it's empty, return N/A
  if (!dateString) {
    return "N/A";
  }

  // Try to parse as ISO date string first, then fallback to timestamp
  let date: Date;

  // Check if it's an ISO date string (contains 'T' or starts with a year)
  if (
    typeof dateString === "string" &&
    (dateString.includes("T") || /^\d{4}-\d{2}-\d{2}/.test(dateString))
  ) {
    date = new Date(dateString);
  } else {
    // Try to parse as timestamp (number)
    const timestamp = Number(dateString);
    if (isNaN(timestamp)) {
      return "N/A";
    }
    date = new Date(timestamp);
  }

  if (isNaN(date.getTime())) {
    return "N/A";
  }

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  const getOrdinalSuffix = (n: number) => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${month} ${day}${getOrdinalSuffix(day)}, ${year}`;
}

/**
 * Formats an ISO date string to a short date format (e.g., "Nov 6, 2025")
 * @param dateString - ISO date string (e.g., "2025-11-06T17:30:37.906Z")
 * @returns Formatted date string or "N/A" if invalid
 */
export function formatDateShort(dateString: string | null | undefined): string {
  if (!dateString) {
    return "N/A";
  }

  try {
    const date =
      typeof dateString === "string" && dateString.includes("T")
        ? parseISO(dateString)
        : new Date(dateString);
    if (!isValid(date)) {
      return "N/A";
    }
    return format(date, "MMM d, yyyy");
  } catch {
    return "N/A";
  }
}

/**
 * Formats an ISO date string to include time (e.g., "Nov 6, 2025 5:30 PM")
 * @param dateString - ISO date string (e.g., "2025-11-06T17:30:37.906Z")
 * @returns Formatted date string with time or "N/A" if invalid
 */
export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) {
    return "N/A";
  }

  try {
    const date =
      typeof dateString === "string" && dateString.includes("T")
        ? parseISO(dateString)
        : new Date(dateString);
    if (!isValid(date)) {
      return "N/A";
    }
    return format(date, "MMM d, yyyy h:mm a");
  } catch {
    return "N/A";
  }
}

/**
 * The single timezone operator-facing timestamps are rendered in, matching the
 * backend's `DISPLAY_TZ`.
 *
 * Without it these formatters follow the viewer's own zone, which is what made
 * the portal and the server-generated report files disagree. Pinning both sides
 * to the same zone is what guarantees a downloaded file matches the table it
 * came from, wherever it is opened from.
 */
export const DISPLAY_TZ = "Asia/Kolkata";

/**
 * "15 Jul 2026, 14:30" — the shared rendering for status timestamps. Kept
 * byte-identical to the backend's `formatOffHistoryDateTime` so the on-screen
 * table and the exported file read the same.
 */
export const formatDisplayDateTime = (iso?: string | null): string => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: DISPLAY_TZ,
  });
};

const displayDateParts = new Intl.DateTimeFormat("en-US", {
  timeZone: DISPLAY_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * "YYYY-MM-DD" for a `<input type="date">` value, in the display timezone.
 *
 * Replaces `date.toISOString().split("T")[0]`, which converts to UTC first: for
 * IST that hands back *yesterday* any time before 05:30, so date pickers
 * defaulted to the wrong day and "max=today" blocked the real today.
 */
export const toDisplayDateInput = (date: Date | string = new Date()): string => {
  const value = date instanceof Date ? date : new Date(date);
  if (isNaN(value.getTime())) return "";
  const parts: Record<string, string> = {};
  for (const part of displayDateParts.formatToParts(value)) {
    if (part.type !== "literal") parts[part.type] = part.value;
  }
  return `${parts.year}-${parts.month}-${parts.day}`;
};

/**
 * Where a creative sits in its flight window, right now.
 *
 * Must stay in step with the backend's `creativeWindow.ts`, which gates what
 * actually reaches a screen: both compare *calendar days in IST*, not instants.
 * The dates are stored as timestamps but a bare "2026-08-17" from the date
 * picker is saved at 00:00 UTC, so an instant comparison would show a creative
 * as Expired from 05:30 IST on the day it is still playing — the badge would
 * contradict the screen.
 *
 * A creative with no dates is Active: unbounded, so neither waiting nor over.
 */
export const getCreativeStatus = (
  startDate?: string | null,
  endDate?: string | null,
): "ACTIVE" | "UPCOMING" | "EXPIRED" => {
  const today = toDisplayDateInput(new Date());
  const start = startDate ? toDisplayDateInput(startDate) : "";
  const end = endDate ? toDisplayDateInput(endDate) : "";

  // ISO "YYYY-MM-DD" sorts lexicographically, so string compare is a date
  // compare — and sidesteps re-parsing into a zone-sensitive Date.
  if (start && start > today) return "UPCOMING";
  if (end && end < today) return "EXPIRED";
  return "ACTIVE";
};

/**
 * Formats a date string into separate date and time components
 * @param dateString - ISO date string or date string
 * @returns Object with date and time properties, or "N/A" for both if invalid
 */
export const formatDateTimeSeparate = (
  dateString?: string
): { date: string; time: string } => {
  if (!dateString) {
    return { date: "N/A", time: "N/A" };
  }
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return { date: "N/A", time: "N/A" };
    }
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  } catch {
    return { date: "N/A", time: "N/A" };
  }
};

/**
 * Formats a screen's lastOnAt date as DD-MM-YYYY. This MUST stay in sync with
 * the backend's per-column date search format (to_char(..., 'DD-MM-YYYY')) so
 * that what the operator sees in the table is exactly what they can search.
 */
export const formatLastOnDate = (
  dateString?: string | null
): string => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  return format(date, "dd-MM-yyyy");
};

/**
 * Formats a screen's lastOnAt time as hh:mm:ss AM/PM. Kept in sync with the
 * backend per-column time search format (to_char(..., 'HH12:MI:SS AM')).
 */
export const formatLastOnTime = (
  dateString?: string | null
): string => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  return format(date, "hh:mm:ss a");
};

/**
 * Formats a date to YYYY-MM-DD format using local timezone
 * @param date - Date object or null/undefined
 * @returns Formatted date string (YYYY-MM-DD) or empty string if invalid
 */
export const formatDateLocal = (
  date: Date | null | undefined
): string => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};
/**
 * "15 Jul 2026" — the date-only companion to `formatDisplayDateTime`, pinned to
 * the same zone for the same reason: a date rendered in the portal must be the
 * same calendar day the server used when it built the report.
 */
export const formatDisplayDate = (iso?: string | null): string => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: DISPLAY_TZ,
  });
};

/**
 * Inclusive count of calendar days spanned by two instants, in the display
 * timezone. A range that starts and ends on the same day is 1 day, not 0.
 *
 * Goes through `toDisplayDateInput` rather than differencing the raw instants:
 * the endpoints are timestamps, so `(end - start) / 86400000` answers "how many
 * 24h periods", which is a different number from "how many calendar days" the
 * moment either endpoint is not at midnight IST.
 *
 * Returns null when either endpoint is missing or unparseable, meaning "no
 * bounded window" — callers should treat that as no limit rather than zero.
 */
export const countDisplayDaysInclusive = (
  start?: string | null,
  end?: string | null,
): number | null => {
  if (!start || !end) return null;
  const startDay = toDisplayDateInput(start);
  const endDay = toDisplayDateInput(end);
  if (!startDay || !endDay) return null;
  const startMs = Date.parse(`${startDay}T00:00:00Z`);
  const endMs = Date.parse(`${endDay}T00:00:00Z`);
  if (isNaN(startMs) || isNaN(endMs)) return null;
  const days = Math.floor((endMs - startMs) / 86400000) + 1;
  return days > 0 ? days : null;
};
